import React, { useState, useEffect } from "react"
import { Loader2, Mail } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"
import { FormItem, FormLabel, FormControl } from "src/components/ui/form"
import { useToast } from "src/components/ui/toast"
import { useUpdateCustomer } from "../hooks/useBookings"

// Edit the customer contact fields on a booking: name, phone, email only.
export function CustomerEditDrawer({ open, booking, onClose, onSuccess }) {
  const { toast } = useToast()
  const { mutate, isPending } = useUpdateCustomer()
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "" })

  useEffect(() => {
    if (open && booking) {
      setForm({
        customer_name: booking.customer?.name || "",
        customer_phone: booking.customer?.phone || "",
        customer_email: booking.customer?.email || "",
      })
    }
  }, [open, booking])

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  // Offer the lead's email when the customer email is blank (item: backfill).
  const leadEmail = booking?.lead?.email
  const canUseLeadEmail = !form.customer_email && leadEmail

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.customer_name.trim()) return
    try {
      await mutate(booking.id, {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim(),
      })
      toast({ title: "Customer updated" })
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not update customer"
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[460px]">
        <SheetHeader>
          <SheetTitle>Edit customer</SheetTitle>
          <SheetDescription>Update the buyer's contact details for this booking.</SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} autoFocus />
              </FormControl>
            </FormItem>
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="10-digit mobile" />
              </FormControl>
            </FormItem>
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="buyer@example.com" />
              </FormControl>
              {canUseLeadEmail && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => set("customer_email", leadEmail)}
                  className="mt-1.5 inline-flex h-auto items-center gap-1 p-0 text-[12px] font-medium text-blue-600 hover:bg-transparent hover:text-blue-700"
                >
                  <Mail className="h-3 w-3" /> Use lead's email ({leadEmail})
                </Button>
              )}
            </FormItem>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>Cancel</Button>
            <Button type="submit" disabled={isPending || !form.customer_name.trim()} className="gap-2">
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
